/**
 * SwotNGuide – förklarar SWOT-processen på rätt nivå.
 *
 * Används på två ställen:
 *   1. I SwotNBoard (facilitator-läge) som en collapsbar hjälppanel
 *   2. Som deltagarvy när man delar en länk (framtida feature)
 *
 * Props:
 *   audience: 'facilitator' | 'participant'  (default: 'facilitator')
 *   compact:  boolean  – kortare version för inline-hjälp (default: false)
 */

import React, { useState } from 'react';
import {
  Box, Typography, IconButton, Collapse, Divider, Dialog, DialogContent,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';

const accent = '#E05500';

// ── Kvadrant-info ──────────────────────────────────────────────────────────────
const QUAD_GUIDE = [
  {
    letter: 'S',
    label:  'Styrkor',
    color:  '#4CAF50',
    short:  'Vad vi är bra på',
    facilitator: 'Interna fördelar som ni råder över. Vad gör er grupp/organisation bra jämfört med andra? Kompetenser, resurser, processer, relationer.',
    participant: 'Vad är ni duktiga på? Vad fungerar bra i vardagen? Vad får ni beröm för?',
    examples: ['Lång erfarenhet av BIM-samordning', 'Starka kundrelationer i regionen', 'Snabbt beslutsfattande'],
  },
  {
    letter: 'W',
    label:  'Svagheter',
    color:  '#EF5350',
    short:  'Vad vi behöver förbättra',
    facilitator: 'Interna brister ni kan påverka. Var halkar ni efter? Vad tar energi utan att ge värde? Anonymt läge rekommenderas – ger ärligare svar.',
    participant: 'Vad är svårt? Var tappar ni tid eller energi? Vad önskar ni fungerade bättre?',
    examples: ['Saknar digital leveransförmåga', 'För beroende av enskilda nyckelpersoner', 'Långa ledtider i offertprocessen'],
  },
  {
    letter: 'O',
    label:  'Möjligheter',
    color:  '#42A5F5',
    short:  'Vad vi kan dra nytta av',
    facilitator: 'Externa faktorer i omvärlden som ni kan utnyttja. Trender, marknadsförändringar, teknikutveckling, nya krav från kunder.',
    participant: 'Vad händer i branschen eller omvärlden som ni kan dra nytta av? Vilka dörrar öppnar sig?',
    examples: ['Ökad efterfrågan på hållbara lösningar', 'Nya offentliga upphandlingar 2025–2026', 'AI-verktyg som effektiviserar administration'],
  },
  {
    letter: 'T',
    label:  'Hot',
    color:  '#E05500',
    short:  'Vad vi behöver skydda oss mot',
    facilitator: 'Externa faktorer som kan skada er. Konkurrenter, regelförändringar, konjunktur, kompetensbrist på marknaden.',
    participant: 'Vad oroar er? Vad kan ställa till det om ni inte hanterar det?',
    examples: ['Ökad konkurrens från rikstäckande aktörer', 'Svårt att rekrytera rätt kompetens', 'Stigande materialkostnader'],
  },
];

// ── Fas-guide för facilitatorn ─────────────────────────────────────────────────
const FACILITATOR_PHASES = [
  {
    number: 1,
    title:  'Formulera frågeställningen',
    duration: '5–10 min',
    color: '#42A5F5',
    desc: 'Den viktigaste, och mest förbisedda, delen av en SWOT. En bra frågeställning ger fokuserade svar. En dålig ger en rörig matris ingen orkar använda.',
    tips: [
      'Var specifik: "Hur stärker vi vår position inom BIM 2026?" slår "Var står vi?"',
      'Dela frågan med gruppen innan ni börjar – alla ska ha samma referensram',
      'Använd AI-knappen för att skärpa formuleringen om du är osäker',
    ],
  },
  {
    number: 2,
    title:  'Tyst individuell insamling',
    duration: '10–15 min',
    color: '#4CAF50',
    desc: 'Låt alla bidra tyst, utan diskussion. Det förhindrar att starka röster dominerar och ger fler perspektiv.',
    tips: [
      'Sätt en timer (10–15 min) – disciplin är bättre än fri tid',
      'Uppmuntra kvantitet framför kvalitet i denna fas – rensning sker sedan',
      'Aktivera anonymt läge på Svagheter och Hot för ärligare svar',
      'Minst 2–3 bidrag per kvadrant är ett bra mål',
    ],
  },
  {
    number: 3,
    title:  'Genomgång, röstning och rankning',
    duration: '20–30 min',
    color: '#FF9800',
    desc: 'Gå igenom korten tillsammans. Klargör, kombinera dubbletter, utmana vaga formuleringar. Rösta sedan fram de viktigaste bidragen.',
    tips: [
      'Läs upp varje kort högt och fråga: "Är vi alla överens om att detta stämmer?"',
      'Flytta kort mellan kvadranter om gruppen ser det annorlunda',
      'Fokusera på styrkor och möjligheter minst 50% av tiden – det är lätt att fastna i problem',
      'RÖSTNING: Varje deltagare kan stjärnmarkera upp till 3 kort per kvadrant via stjärn-ikonen. Antalet röster visas på kortet.',
      'RANKNING: Öppna rankning-panelen (trofé-ikonen i toolbaren). Klicka "Beräkna från röster" för att automatiskt tilldela guld/silver/brons baserat på röstresultaten — eller sätt rankningarna manuellt via trofé-ikonen på varje enskilt kort.',
      'Rankade kort (guld/silver/brons) lyfts visuellt i boardet och behåller sin status i presentationsläget.',
    ],
  },
  {
    number: 4,
    title:  'AI-analys och korskopplingar',
    duration: '5–10 min',
    color: accent,
    desc: 'Det är i korsningarna mellan kvadranterna som strategin föds. AI hjälper er att se mönster ni kanske missar.',
    tips: [
      'SO = Offensiv strategi: Hur kan vi använda våra styrkor för att ta tillvara möjligheterna?',
      'ST = Defensiv strategi: Hur kan vi använda våra styrkor för att möta hoten?',
      'WO = Utvecklingsstrategi: Hur kan vi åtgärda svagheter för att fånga möjligheterna?',
      'WT = Krisstrategi: Hur minimerar vi svagheter och undviker hot?',
    ],
  },
  {
    number: 5,
    title:  'Åtgärder med ägare och datum',
    duration: '10–15 min',
    color: '#4CAF50',
    desc: 'En SWOT utan åtgärder är bara en övning. Varje åtgärd behöver en ansvarig och ett datum för att bli verklighet.',
    tips: [
      'Max 3–5 prioriterade åtgärder – bättre att slutföra få än att ha en lång lista',
      'Varje åtgärd ska ha ett namn som äger den, inte "vi" eller "teamet"',
      'Sätt ett konkret datum, inte "Q3" – specifika datum håller bättre',
      'Följ upp på nästa möte – annars var det bortkastad tid',
    ],
  },
];

// ── Kvadrant-kort ──────────────────────────────────────────────────────────────
function QuadCard({ q, audience }) {
  const text = audience === 'participant' ? q.participant : q.facilitator;
  return (
    <Box sx={{
      p: 1.5, borderRadius: 1.5,
      border: `1px solid ${q.color}33`,
      bgcolor: q.color + '0a',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Box sx={{
          width: 24, height: 24, borderRadius: '6px',
          bgcolor: q.color + '25', border: `1px solid ${q.color}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.8rem', color: q.color }}>{q.letter}</Typography>
        </Box>
        <Typography fontWeight={700} fontSize="0.82rem" color={q.color}>{q.label}</Typography>
        <Typography fontSize="0.72rem" color="text.disabled">— {q.short}</Typography>
      </Box>
      <Typography fontSize="0.8rem" color="text.secondary" lineHeight={1.5} mb={0.75}>{text}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {q.examples.map((ex, i) => (
          <Box key={i} sx={{
            px: 0.75, py: 0.2, borderRadius: 1,
            bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider',
          }}>
            <Typography fontSize="0.66rem" color="text.secondary" fontStyle="italic">"{ex}"</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Fas-kort ──────────────────────────────────────────────────────────────────
function PhaseCard({ phase }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
          cursor: 'pointer', bgcolor: 'action.hover',
          '&:hover': { bgcolor: 'action.selected' },
        }}
      >
        <Box sx={{
          width: 24, height: 24, borderRadius: '50%',
          bgcolor: phase.color + '22', border: `1px solid ${phase.color}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.72rem', color: phase.color }}>{phase.number}</Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={700} fontSize="0.85rem">{phase.title}</Typography>
          <Typography fontSize="0.68rem" color="text.disabled">{phase.duration}</Typography>
        </Box>
        {open ? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.disabled' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ p: 1.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography fontSize="0.82rem" color="text.secondary" lineHeight={1.55} mb={1}>
            {phase.desc}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {phase.tips.map((tip, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: phase.color, mt: '7px', flexShrink: 0 }} />
                <Typography fontSize="0.78rem" color="text.secondary" lineHeight={1.5}>{tip}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Huvud-komponent ────────────────────────────────────────────────────────────
export default function SwotNGuide({ audience = 'facilitator', compact = false, onClose }) {
  return (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <HelpOutlineIcon sx={{ color: accent, fontSize: 18 }} />
            <Typography fontWeight={700} fontSize="0.95rem">
              {audience === 'participant' ? 'Vad är en SWOT?' : 'Guide: Leda en SWOT-analys'}
            </Typography>
          </Box>
          <Typography fontSize="0.75rem" color="text.secondary">
            {audience === 'participant'
              ? 'Din guide som deltagare'
              : 'Steg-för-steg för dig som faciliterar'}
          </Typography>
        </Box>
        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ color: 'text.disabled', mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Vad är SWOT – alltid synligt */}
      <Box sx={{ p: 1.75, bgcolor: accent + '0d', border: `1px solid ${accent}33`, borderRadius: 1.5 }}>
        <Typography fontWeight={700} fontSize="0.82rem" mb={0.75}>Vad är en SWOT-analys?</Typography>
        <Typography fontSize="0.8rem" color="text.secondary" lineHeight={1.6}>
          En SWOT-analys är ett strukturerat sätt för ett team att kartlägga sin situation och fatta bättre strategiska beslut.
          Matrisen delar upp läget i fyra kvadranter: <strong>Styrkor</strong> och <strong>Svagheter</strong> handlar om det
          interna — sådant ni råder över. <strong>Möjligheter</strong> och <strong>Hot</strong> handlar om det externa
          — omvärlden och marknaden.
        </Typography>
        {!compact && (
          <Typography fontSize="0.78rem" color="text.secondary" lineHeight={1.6} mt={0.75}>
            Värdet ligger inte i att fylla i rutorna — det ligger i <em>diskussionen</em> ni för och i de
            korskopplingar ni hittar. En Styrka som matchar en Möjlighet är en offensiv strategi att ta tillvara.
            En Svaghet som möter ett Hot är en kritisk risk att prioritera.
          </Typography>
        )}
      </Box>

      <Divider />

      {/* Kvadrant-guide */}
      <Box>
        <Typography fontSize="0.72rem" fontWeight={700} color="text.secondary" letterSpacing={0.5} mb={1}>
          {audience === 'participant' ? 'VAD SKA DU BIDRA MED?' : 'DE FYRA KVADRANTERNA'}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {QUAD_GUIDE.map(q => (
            <QuadCard key={q.letter} q={q} audience={audience} />
          ))}
        </Box>
      </Box>

      {/* Fas-guide (bara för facilitator, och bara full version) */}
      {audience === 'facilitator' && !compact && (
        <>
          <Divider />
          <Box>
            <Typography fontSize="0.72rem" fontWeight={700} color="text.secondary" letterSpacing={0.5} mb={1}>
              PROCESSEN — FEM FASER
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {FACILITATOR_PHASES.map(p => (
                <PhaseCard key={p.number} phase={p} />
              ))}
            </Box>
          </Box>

          <Divider />

          {/* Vanliga misstag */}
          <Box>
            <Typography fontSize="0.72rem" fontWeight={700} color="text.secondary" letterSpacing={0.5} mb={1}>
              VANLIGA MISSTAG ATT UNDVIKA
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {[
                { mistake: 'Hoppa över frågeställningen', fix: 'Investera 5–10 min på att formulera en skarp fråga. Det är det viktigaste du gör.' },
                { mistake: 'Diskutera medan ni fyller i', fix: 'Håll insamlingen tyst. Diskussion under insamling tystar blyga deltagare.' },
                { mistake: 'Sluta efter genomgången', fix: 'En SWOT utan åtgärder är bortkastad tid. Minst 3 konkreta handlingar med ägare.' },
                { mistake: 'Fylla alla rutor lika', fix: 'Matrisen behöver inte vara symmetrisk. En insiktsfull Styrka är bättre än fem vaga.' },
                { mistake: 'Aldrig följa upp', fix: 'Boka in uppföljning direkt. 4–6 veckor är lagom för att se om åtgärder genomförs.' },
              ].map(({ mistake, fix }, i) => (
                <Box key={i} sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
                  <Typography fontSize="0.8rem" fontWeight={700} sx={{ color: '#ef4444', mb: 0.25 }}>✕ {mistake}</Typography>
                  <Typography fontSize="0.78rem" color="text.secondary">→ {fix}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}

      {/* Tidsestimering */}
      {!compact && (
        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
          <Typography fontSize="0.72rem" fontWeight={700} color="text.secondary" letterSpacing={0.5} mb={0.75}>
            TIDSPLAN (RIKTLINJE)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {[
              { label: 'Snabb (60 min)', desc: 'Lagom för ett team som känner sig – bra för retrospektiv eller snabb omvärldsbild' },
              { label: 'Standard (90 min)', desc: 'Rekommenderat för de flesta workshops – ger tid för diskussion och åtgärder' },
              { label: 'Djupdyk (120+ min)', desc: 'Används när beslutet är strategiskt viktigt eller gruppen är stor' },
            ].map(t => (
              <Box key={t.label} sx={{ flex: '1 1 140px' }}>
                <Typography fontSize="0.75rem" fontWeight={700} color={accent} mb={0.25}>{t.label}</Typography>
                <Typography fontSize="0.72rem" color="text.secondary" lineHeight={1.4}>{t.desc}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Inline-hjälpknapp (används i SwotNBoard header) ────────────────────────────
export function SwotNHelpButton({ audience = 'facilitator' }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <IconButton
        size="small"
        onClick={() => setOpen(true)}
        sx={{ color: 'rgba(255,255,255,0.45)', '&:hover': { color: '#fff' } }}
        title="Hjälp"
      >
        <HelpOutlineIcon fontSize="small" />
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '88vh' } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <SwotNGuide audience={audience} onClose={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}
